/* Fitment Lab — live chat widget + lead capture popup.
 *
 * Talks to the same backend brain as the Facebook Messenger bot
 * (webchat.py blueprint on the bot.py service), so a shopper on the site and a
 * shopper in Messenger are handled identically and land in the same CRM.
 *
 * Open/close of the panel is still owned by fitment-theme.js (setChat), which
 * toggles [data-chat-panel].hidden — we only observe it so we can focus the
 * input and mark the greeting as seen.
 */
(function () {
  'use strict';

  var ENDPOINT = (window.FL_CHAT_ENDPOINT || 'https://webhook.fitmentlabs.com')
    .replace(/\/+$/, '');
  var SESSION_KEY = 'fl_chat_session';
  var CAPTURED_KEY = 'fl_lead_captured';
  var HISTORY_KEY = 'fl_chat_history';

  function store(key, val) {
    try { localStorage.setItem(key, val); } catch (e) { /* private mode */ }
  }
  function read(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function sessionId() {
    var id = read(SESSION_KEY);
    if (!id) {
      id = 'w' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      store(SESSION_KEY, id);
    }
    return id;
  }

  /* ── Chat ─────────────────────────────────────────────────────────────── */

  var log = document.querySelector('[data-chat-log]');
  var form = document.querySelector('[data-chat-form]');
  var input = document.querySelector('[data-chat-input]');
  var panel = document.querySelector('[data-chat-panel]');
  var quick = document.querySelector('[data-chat-quick]');
  var busy = false;

  function scrollLog() {
    if (log) log.scrollTop = log.scrollHeight;
  }

  function addMsg(text, who) {
    if (!log) return null;
    var el = document.createElement('div');
    el.className = 'co-msg co-msg--' + who;
    el.textContent = text;
    log.appendChild(el);
    scrollLog();
    return el;
  }

  function saveHistory() {
    if (!log) return;
    var items = [];
    log.querySelectorAll('.co-msg').forEach(function (m) {
      items.push({
        w: m.classList.contains('co-msg--user') ? 'user' : 'bot',
        t: m.textContent.trim()
      });
    });
    store(HISTORY_KEY, JSON.stringify(items.slice(-40)));
  }

  function restoreHistory() {
    var raw = read(HISTORY_KEY);
    if (!raw || !log) return;
    try {
      var items = JSON.parse(raw);
      if (!items || !items.length) return;
      log.innerHTML = '';
      items.forEach(function (i) { addMsg(i.t, i.w); });
    } catch (e) { /* ignore corrupt history */ }
  }

  function typing() {
    if (!log) return null;
    var el = document.createElement('div');
    el.className = 'co-msg co-msg--bot co-msg--typing';
    el.innerHTML = '<i></i><i></i><i></i>';
    log.appendChild(el);
    scrollLog();
    return el;
  }

  function send(text) {
    if (!text || busy) return;
    busy = true;
    addMsg(text, 'user');
    if (quick) quick.hidden = true;
    var dots = typing();

    fetch(ENDPOINT + '/chat/message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId(), message: text })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (dots) dots.remove();
        addMsg(data.reply || 'Sorry — try again?', 'bot');
        if (data.escalated) {
          var note = document.createElement('div');
          note.className = 'co-msg co-msg--note';
          note.innerHTML = 'A specialist will follow up. Need it now? ' +
            '<a href="tel:9729270649">Call 972-927-0649</a>';
          log.appendChild(note);
          scrollLog();
        }
        saveHistory();
      })
      .catch(function () {
        if (dots) dots.remove();
        addMsg('Connection hiccup — call or text us at 972-927-0649 and we’ll ' +
               'sort it out.', 'bot');
      })
      .then(function () {
        busy = false;
        if (input) input.focus();
      });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var text = (input.value || '').trim();
      if (!text) return;
      input.value = '';
      send(text);
    });
  }

  if (quick) {
    quick.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-chat-preset]');
      if (!btn) return;
      send(btn.getAttribute('data-chat-preset'));
    });
  }

  // fitment-theme.js owns the hidden toggle; react to it.
  if (panel && window.MutationObserver) {
    restoreHistory();
    new MutationObserver(function () {
      if (!panel.hidden && input) {
        setTimeout(function () { input.focus(); }, 60);
      }
    }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
  }

  /* ── Lead capture popup ───────────────────────────────────────────────── */

  var pop = document.querySelector('[data-lead-pop]');
  var leadForm = document.querySelector('[data-lead-form]');
  var leadMsg = document.querySelector('[data-lead-msg]');
  var phoneInput = leadForm ? leadForm.querySelector('[name=phone]') : null;
  var consentRow = document.querySelector('[data-lead-consent-row]');
  var consentBox = document.querySelector('[data-lead-consent]');

  function captured() {
    return read(CAPTURED_KEY) === '1';
  }

  function showPop() {
    if (!pop || captured()) return;
    if (panel && !panel.hidden) return;   // never fight an open chat
    pop.hidden = false;
    document.body.classList.add('co-lead-open');
  }

  function hidePop() {
    if (!pop) return;
    pop.hidden = true;
    document.body.classList.remove('co-lead-open');
  }

  if (pop && window.FL_LEAD_POPUP_ENABLED !== false && !captured()) {
    var delay = (window.FL_LEAD_POPUP_DELAY || 10) * 1000;
    setTimeout(showPop, delay);

    // Exit intent — a second bite on the same pageview.
    document.addEventListener('mouseout', function (e) {
      if (e.clientY <= 0 && !captured()) showPop();
    });
  }

  if (pop) {
    pop.addEventListener('click', function (e) {
      if (e.target.closest('[data-lead-close]') || e.target === pop) hidePop();
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && pop && !pop.hidden) hidePop();
  });

  /*
   * Real, explicit SMS opt-in (2026-09-18): the consent checkbox is only
   * relevant once a phone number is actually entered, so it stays hidden
   * until then, and gets unchecked again if the phone field is cleared —
   * checking it once should not silently carry consent for a different
   * number typed in later.
   */
  function syncConsentVisibility() {
    if (!consentRow || !phoneInput) return;
    var hasPhone = Boolean((phoneInput.value || '').trim());
    consentRow.hidden = !hasPhone;
    if (!hasPhone && consentBox) consentBox.checked = false;
  }

  if (phoneInput) {
    phoneInput.addEventListener('input', syncConsentVisibility);
    syncConsentVisibility();
  }

  if (leadForm) {
    leadForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (leadForm.querySelector('[name=email]').value || '').trim();
      var phone = (phoneInput && phoneInput.value || '').trim();
      if (!email && !phone) return;

      // A phone number is only captured for SMS once the visitor has
      // actually checked the consent box themselves — clicking
      // "Claim 10% off" alone is no longer treated as SMS consent.
      var smsConsent = Boolean(consentBox && consentBox.checked);
      if (phone && !smsConsent) {
        if (consentRow) consentRow.hidden = false;
        if (leadMsg) {
          leadMsg.textContent = 'Please check the box to consent to SMS before submitting your ' +
            'number — or leave the phone field blank and we will only email your code.';
        }
        return;
      }

      var btn = leadForm.querySelector('.co-lead-submit');
      if (btn) { btn.disabled = true; btn.textContent = 'SENDING…'; }

      fetch(ENDPOINT + '/chat/capture', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId(),
          email: email,
          phone: phone,
          sms_consent: smsConsent,
          sms_consent_at: smsConsent ? new Date().toISOString() : null,
          source: 'popup'
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.ok) {
            store(CAPTURED_KEY, '1');
            leadForm.innerHTML =
              '<div class="co-lead-done">You’re in! 🔥<br>' +
              'We’ll text your 10% off code and fitment help shortly.</div>';
            if (leadMsg) leadMsg.textContent = '';
            setTimeout(hidePop, 6000);
          } else {
            throw new Error('rejected');
          }
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = 'CLAIM 10% OFF'; }
          if (leadMsg) {
            leadMsg.textContent =
              'Something went wrong — try again, or text us at 972-927-0649.';
          }
        });
    });
  }
})();
